import { Alert, Button, Form, ProgressBar } from 'react-bootstrap';
import {
  FEMA_PASSWORD_LABELS,
  FEMA_REVIEW_CONFIRM_LABEL,
} from '../../../../config/femaCredentials.config';
import { formatCredentialDate } from '../../../../utils/credentialState.utils';
import PasswordFields, { passwordsReady } from '../PasswordStep/PasswordFields';

/**
 * Why the Review step refuses to continue, or `null`. The backend refuses the same cases by
 * stage (`cog-mismatch`, `expired`, `not-yet-valid`); this check stops the person earlier.
 */
const reviewBlock = (inspection, now = Date.now()) => {
  if (inspection.cogMatches === false) {
    return 'The certificate is not for this COG. Upload the certificate FEMA issued for this COG.';
  }
  if (inspection.daysToExpiry < 0) return 'The certificate expired. Upload a valid certificate.';
  const notBefore = new Date(inspection.notBefore).getTime();
  if (!Number.isNaN(notBefore) && notBefore > now) {
    return 'The certificate is not valid yet. Upload a certificate that is valid now.';
  }
  return null;
};

/**
 * Step 3 — Review (design read §3.4). It shows the facts the backend read in INSPECT mode. The
 * Confirm button stays disabled until the person ticks "This is my organization's certificate."
 * Confirm stores the credential with `confirmSha256` = the fingerprint on screen.
 *
 * @param {{ upload: import('../../hooks/useCredentialUpload').CredentialUploadState }} props
 */
const ReviewStep = ({ upload }) => {
  const { inspection } = upload;
  if (!inspection) return null;

  const kind = upload.files?.kind ?? 'keystore';
  const block = reviewBlock(inspection);
  const rows = [
    ['Subject', inspection.subject],
    ['Issuer', inspection.issuer],
    ['Serial number', inspection.serialNumber],
    ['SHA-256 fingerprint', inspection.sha256],
    ['Valid from', formatCredentialDate(inspection.notBefore)],
    ['Valid to', formatCredentialDate(inspection.notAfter)],
    ['Days to expiry', String(inspection.daysToExpiry)],
    ['Key algorithm', inspection.keyAlgorithm],
    ['Chain certificates', String(inspection.chainLength)],
    [
      'COG matches',
      inspection.cogMatches === null ? 'Not checked' : inspection.cogMatches ? 'Yes' : 'No',
    ],
  ];

  const submit = (event) => {
    event.preventDefault();
    if (!block && upload.confirmed && passwordsReady(kind, upload.filled)) upload.store();
  };

  return (
    <Form onSubmit={submit} autoComplete="off">
      <dl className="row small">
        {rows.map(([label, value]) => (
          <div key={label} className="col-12 d-flex flex-wrap">
            <dt className="col-sm-4">{label}</dt>
            <dd className="col-sm-8 mb-1 text-break font-monospace">{value}</dd>
          </div>
        ))}
      </dl>

      {block ? (
        <Alert variant="danger" className="mb-3">
          {block}
        </Alert>
      ) : (
        <>
          <Form.Check
            className="mb-3"
            type="checkbox"
            id="fema-credential-confirm"
            label={FEMA_REVIEW_CONFIRM_LABEL}
            checked={upload.confirmed}
            onChange={(event) => upload.setConfirmed(event.target.checked)}
          />
          <p className="small">{FEMA_PASSWORD_LABELS.reenterHelp}</p>
          <PasswordFields
            kind={kind}
            idPrefix="fema-credential-store"
            fieldRefs={upload.passwordFields}
            onChange={upload.setPassword}
          />
        </>
      )}

      {upload.progress !== null ? (
        <ProgressBar
          className="mb-3"
          now={Math.round(upload.progress * 100)}
          label={`${Math.round(upload.progress * 100)}%`}
          aria-label="Upload progress"
        />
      ) : null}

      <div className="d-flex gap-2">
        <Button variant="outline-secondary" onClick={upload.back} disabled={upload.busy}>
          Back
        </Button>
        <Button
          type="submit"
          disabled={
            Boolean(block) ||
            !upload.confirmed ||
            upload.busy ||
            !passwordsReady(kind, upload.filled)
          }
        >
          Confirm and store
        </Button>
      </div>
    </Form>
  );
};

export default ReviewStep;
